/**
 * Responsibility: pull a card you were already watching onto the grading list.
 *
 * Two lists feed it. The discovery board's "watching" bin holds cards taken
 * off the screen deliberately, and the search page's likes hold cards found
 * by name. Both mean the same thing - this one is interesting - and the
 * next question about an interesting raw card is whether it is worth
 * sending to PSA. Until now that meant reading a name off one page and
 * typing it into another.
 *
 * Adding is one row at a time and stays open. Each add prices the card
 * against live listings, so a "add all" button would fire a dozen
 * sequential catalogue reads off one click with no way to stop it.
 *
 * Rows already on the grading list are shown as added rather than hidden.
 * A card vanishing from the list you are reading is how you lose your place
 * in it.
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import type { ArchivedCard, LikedCard } from '../../api/client';
import { CardThumb } from './CardThumb';
import { money } from './money';

/** The body is a fixed height so switching tabs does not resize the box. */
const BODY_HEIGHT = { xs: '56vh', md: 'min(440px, 60vh)' };

const THUMB_WIDTH = 44;

type FromWatchedDialogProps = {
  open: boolean;
  watched: ArchivedCard[];
  likes: LikedCard[];
  /** Card names already on the grading list, lowercased. */
  already: Set<string>;
  busy: boolean;
  onClose: () => void;
  onAddWatched: (discoveryKey: string) => void;
  onAddLike: (productKey: string) => void;
};

/** Lowercased, because the two sources capitalise differently. */
function nameKey(value: string): string {
  return value.trim().toLowerCase();
}

type RowProps = {
  title: string;
  detail: string;
  imageUrl?: string | null;
  added: boolean;
  busy: boolean;
  onAdd: () => void;
};

function Row({ title, detail, imageUrl, added, busy, onAdd }: RowProps) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider', minWidth: 0 }}
    >
      {imageUrl === undefined ? null : (
        <CardThumb src={imageUrl} alt={title} width={THUMB_WIDTH} />
      )}
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {detail}
        </Typography>
      </Stack>
      {added ? (
        <Chip
          size="small"
          variant="outlined"
          icon={<CheckIcon sx={{ fontSize: 15 }} />}
          label="On the list"
        />
      ) : (
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={busy}
          onClick={onAdd}
          sx={{ flexShrink: 0 }}
        >
          Consider
        </Button>
      )}
    </Stack>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', height: '100%', px: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        {message}
      </Typography>
    </Box>
  );
}

export function FromWatchedDialog({
  open,
  watched,
  likes,
  already,
  busy,
  onClose,
  onAddWatched,
  onAddLike,
}: FromWatchedDialogProps) {
  const [tab, setTab] = useState(0);

  const tabs = [
    {
      label: watched.length > 0 ? `Watching (${watched.length})` : 'Watching',
      content:
        watched.length === 0 ? (
          <Empty message="Nothing on the discovery board's watching list. Use the eye on a discovery row to put a card there." />
        ) : (
          <Stack>
            {watched.map((card) => (
              <Row
                key={card.discovery_key}
                title={card.card_name}
                detail={`${card.set_name} #${card.card_number} - PSA 10 pop ${card.pop_psa10}${
                  card.latest_psa10 ? `, last ${money(card.latest_psa10)}` : ''
                }`}
                added={already.has(nameKey(card.card_name))}
                busy={busy}
                onAdd={() => onAddWatched(card.discovery_key)}
              />
            ))}
          </Stack>
        ),
    },
    {
      label: likes.length > 0 ? `Liked (${likes.length})` : 'Liked',
      content:
        likes.length === 0 ? (
          <Empty message="Nothing liked yet. The heart on any search row keeps a card for later." />
        ) : (
          <Stack>
            {likes.map((card) => (
              <Row
                key={card.product_key}
                title={card.name}
                detail={
                  card.baseline_value
                    ? `${money(card.baseline_value)} at ${card.baseline_label} when liked`
                    : 'No price recorded when liked'
                }
                imageUrl={card.image_url}
                // Matched on the catalogue name, which carries the number
                // and set too, so this only catches an exact repeat.
                added={already.has(nameKey(card.name.split('#')[0]))}
                busy={busy}
                onAdd={() => onAddLike(card.product_key)}
              />
            ))}
          </Stack>
        ),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Add from what you are watching</DialogTitle>

      <Box sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_event, next: number) => setTab(next)}>
          {tabs.map((entry) => (
            <Tab key={entry.label} label={entry.label} />
          ))}
        </Tabs>
      </Box>

      {/* Fixed height with its own scroll: these lists are unbounded, and
          a dialog that grows with them would run off the screen. */}
      <DialogContent sx={{ height: BODY_HEIGHT, overflowY: 'auto', pt: 1 }}>
        {tabs[tab]?.content}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Each card is priced against live listings as it is added.
        </Typography>
        <Button size="small" variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
